import React, { useMemo, useState } from 'react';
import { Alert, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronRight,
  faFolder,
  faFolderOpen,
} from '@fortawesome/free-solid-svg-icons';

import {
  getFileFolderLabel,
  getFileFolderPath,
  getFileFolderSegments,
} from '../../fileBrowser/fileFolderUtils';

const FOLDER_INDENT_PX = 16;

const createFolderNode = (path) => ({
  path,
  folders: new Map(),
  files: [],
});

const buildEvidenceTree = (files) => {
  const root = createFolderNode('root');

  files.forEach((file) => {
    const segments = getFileFolderSegments(file);
    let currentNode = root;
    let currentPath = '';

    segments.forEach((segment) => {
      currentPath = currentPath ? `${currentPath}/${segment}` : segment;
      if (!currentNode.folders.has(currentPath)) {
        currentNode.folders.set(currentPath, createFolderNode(currentPath));
      }
      currentNode = currentNode.folders.get(currentPath);
    });

    currentNode.files.push(file);
  });

  return root;
};

const sortFolderNode = (node) => ({
  ...node,
  files: [...node.files].sort((a, b) => String(a.fileName || '').localeCompare(String(b.fileName || ''))),
  folders: [...node.folders.values()]
    .sort((a, b) => getFileFolderLabel(a.path).localeCompare(getFileFolderLabel(b.path)))
    .map(sortFolderNode),
});

const matchesEvidenceSearch = (file, searchTerm) => {
  if (!searchTerm) return true;

  const normalizedSearch = String(searchTerm || '').trim().toLowerCase();
  if (!normalizedSearch) return true;

  const haystack = [
    file?.fileName,
    getFileFolderPath(file),
    ...getFileFolderSegments(file).map((segment, index, segments) =>
      getFileFolderLabel(segments.slice(0, index + 1).join('/')),
    ),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return haystack.includes(normalizedSearch);
};

const EvidenceFolderNode = ({
  node,
  depth = 0,
  selectedEvidenceFileIds = [],
  isSavingRequest = false,
  onEvidenceToggle,
  expandedFolders = {},
  onToggleFolder,
  forceExpanded = false,
}) => {
  const isRoot = node.path === 'root';
  const isExpanded = isRoot || forceExpanded || expandedFolders[node.path] !== false;
  const childDepth = depth + (isRoot ? 0 : 1);

  return (
    <div>
      {!isRoot ? (
        <button
          type="button"
          className="d-flex align-items-center gap-2 fw-semibold small text-uppercase text-muted mb-2 border-0 bg-transparent p-0"
          style={{ paddingLeft: `${depth * FOLDER_INDENT_PX}px` }}
          onClick={() => onToggleFolder?.(node.path)}
          disabled={forceExpanded}
        >
          <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} />
          <FontAwesomeIcon icon={isExpanded ? faFolderOpen : faFolder} />
          <span>{getFileFolderLabel(node.path)}</span>
        </button>
      ) : null}

      {isExpanded ? (
        <>
          {node.files.map((file) => (
            <div
              key={file.fileId}
              style={{ paddingLeft: `${childDepth * FOLDER_INDENT_PX}px` }}
            >
              <Form.Check
                id={`dbq-request-evidence-${file.fileId}`}
                type="checkbox"
                className="mb-2"
                label={
                  <span>
                    {file.fileName}
                    {file.fileVersion ? (
                      <span className="text-muted small"> (v{file.fileVersion})</span>
                    ) : null}
                  </span>
                }
                checked={selectedEvidenceFileIds.includes(file.fileId)}
                onChange={(event) => onEvidenceToggle(file.fileId, event.target.checked)}
                disabled={isSavingRequest}
              />
            </div>
          ))}

          {node.folders.map((childNode) => (
            <EvidenceFolderNode
              key={childNode.path}
              node={childNode}
              depth={childDepth}
              selectedEvidenceFileIds={selectedEvidenceFileIds}
              isSavingRequest={isSavingRequest}
              onEvidenceToggle={onEvidenceToggle}
              expandedFolders={expandedFolders}
              onToggleFolder={onToggleFolder}
              forceExpanded={forceExpanded}
            />
          ))}
        </>
      ) : null}
    </div>
  );
};

const DbqEvidencePackagePicker = ({
  claimEvidenceFiles = [],
  selectedEvidenceFileIds = [],
  isSavingRequest = false,
  onEvidenceToggle,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedFolders, setExpandedFolders] = useState({});

  const filteredFiles = useMemo(
    () => claimEvidenceFiles.filter((file) => matchesEvidenceSearch(file, searchTerm)),
    [claimEvidenceFiles, searchTerm],
  );

  const evidenceTree = useMemo(
    () => sortFolderNode(buildEvidenceTree(filteredFiles)),
    [filteredFiles],
  );
  const forceExpanded = Boolean(String(searchTerm || '').trim());

  const handleToggleFolder = (folderPath) => {
    setExpandedFolders((prev) => ({
      ...prev,
      [folderPath]: prev[folderPath] === false,
    }));
  };

  if (claimEvidenceFiles.length === 0) {
    return (
      <Alert variant="light" className="mb-0">
        No claim files are available to package yet.
      </Alert>
    );
  }

  return (
    <div className="border rounded p-3">
      <Form.Control
        type="search"
        placeholder="Search files or folders..."
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        className="mb-3"
        disabled={isSavingRequest}
      />

      <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
        {filteredFiles.length === 0 ? (
          <div className="text-muted small">No files match your search.</div>
        ) : (
          <EvidenceFolderNode
            node={evidenceTree}
            selectedEvidenceFileIds={selectedEvidenceFileIds}
            isSavingRequest={isSavingRequest}
            onEvidenceToggle={onEvidenceToggle}
            expandedFolders={expandedFolders}
            onToggleFolder={handleToggleFolder}
            forceExpanded={forceExpanded}
          />
        )}
      </div>
    </div>
  );
};

export default DbqEvidencePackagePicker;
